/**
 * CustomerActivityFormSheet - Sheet lateral com formulário de atividade para cliente
 * Similar ao ActivityFormSheet de deals, mas adaptado para contexto de cliente
 * Cria atividades vinculadas ao primeiro deal do cliente
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10
 */
import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Phone, 
  Calendar, 
  Mail, 
  CheckSquare, 
  FileText,
  Loader2,
  Save,
  Paperclip,
  Upload,
  X,
  File
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useActivityAttachments, formatFileSize } from '@/hooks/useActivityAttachments';
// v1.1.0: criação via RPC create_customer_activity (ponto único no banco)
import { useCustomerActivityCreate } from '@/hooks/useCustomerActivityCreate';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import type { QuickActivityType } from '@/components/crm/deals/modal/components/QuickActivityBar';

// Logger do módulo
const logger = createLogger('CustomerActivityFormSheet');

// Schema de validação do formulário
const activityFormSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório').max(100, 'Título muito longo'),
  description: z.string().max(5000, 'Descrição muito longa').optional(),
  scheduled_at: z.string().optional(),
});

type ActivityFormData = z.infer<typeof activityFormSchema>;

// Interface para arquivo pendente
interface PendingFile {
  id: string;
  file: File;
  preview?: string;
}

// Props do componente
interface CustomerActivityFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activityType: QuickActivityType | null;
  customerId: string;
  onSuccess?: () => void;
}

// Configuração visual por tipo de atividade (idêntica ao de deals)
const activityConfig: Record<Exclude<QuickActivityType, 'scheduling' | 'contracts'>, {
  icon: typeof Phone;
  title: string;
  titlePlaceholder: string;
  color: string;
  bgClass: string;
  showSchedule: boolean;
  showRichText: boolean;
  showAttachments: boolean;
}> = {
  call: {
    icon: Phone,
    title: 'Nova Ligação',
    titlePlaceholder: 'Ex: Ligação de follow-up',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
    showSchedule: false,
    showRichText: false,
    showAttachments: false,
  },
  meeting: {
    icon: Calendar,
    title: 'Nova Reunião',
    titlePlaceholder: 'Ex: Reunião de apresentação',
    color: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    showSchedule: true,
    showRichText: false,
    showAttachments: false,
  },
  email: {
    icon: Mail,
    title: 'Novo E-mail',
    titlePlaceholder: 'Ex: Envio de proposta',
    color: 'text-violet-600 dark:text-violet-400',
    bgClass: 'bg-violet-100 dark:bg-violet-900/30',
    showSchedule: false,
    showRichText: false,
    showAttachments: false,
  },
  task: {
    icon: CheckSquare,
    title: 'Novo Lembrete',
    titlePlaceholder: 'Ex: Preparar documentação',
    color: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    showSchedule: true,
    showRichText: false,
    showAttachments: false,
  },
  note: {
    icon: FileText,
    title: 'Nova Nota',
    titlePlaceholder: 'Ex: Resumo da conversa',
    color: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    showSchedule: false,
    showRichText: true,
    showAttachments: true,
  },
};

/**
 * Sheet lateral com formulário específico para cada tipo de atividade (contexto de cliente)
 */
export function CustomerActivityFormSheet({
  open,
  onOpenChange,
  activityType,
  customerId,
  onSuccess
}: CustomerActivityFormSheetProps) {
  // Estados locais
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [richDescription, setRichDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Context
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Hook de anexos (usado após criar atividade)
  const { uploadFile } = useActivityAttachments(null);
  const { createActivity } = useCustomerActivityCreate(customerId);

  // Form
  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activityFormSchema),
    defaultValues: {
      title: '',
      description: '',
      scheduled_at: '',
    },
  });

  // Reset form quando o tipo muda ou abre
  React.useEffect(() => {
    if (open) {
      form.reset({
        title: '',
        description: '',
        scheduled_at: '',
      });
      setRichDescription('');
      setPendingFiles([]);
    }
  }, [open, activityType, form]);

  // Se não tem tipo, não renderiza
  if (!activityType) return null;

  const config = activityConfig[activityType];
  const Icon = config.icon;

  // Handler para adicionar arquivos
  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;

    const newFiles: PendingFile[] = [];
    
    Array.from(files).forEach(file => {
      // Valida tamanho (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Arquivo muito grande (máx 10MB)`);
        return;
      }

      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Cria preview para imagens
      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      newFiles.push({ id, file, preview });
    });

    setPendingFiles(prev => [...prev, ...newFiles].slice(0, 10)); // Máximo 10 arquivos
  };

  // Handler para remover arquivo pendente
  const removePendingFile = (id: string) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  // Handler de drag and drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFilesSelected(e.dataTransfer.files);
  };

  // Submit do formulário
  const onSubmit = async (data: ActivityFormData) => {
    if (!currentTenant?.id) {
      toast.error('Tenant não identificado');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // v1.1.0: criação agora é feita pela RPC `create_customer_activity`
      // (SECURITY DEFINER). O vínculo com o negócio mais recente do cliente,
      // a validação de tenant/ownership e a whitelist de tipos ficam no banco,
      // eliminando o INSERT direto que existia aqui.
      const description = config.showRichText ? richDescription : data.description;

      const activityId = await createActivity.mutateAsync({
        customer_id: customerId,
        activity_type: activityType,
        title: data.title,
        description: description || null,
        scheduled_at: data.scheduled_at || null,
      });

      // Upload dos anexos (se houver)
      if (pendingFiles.length > 0 && activityId) {
        const uploadPromises = pendingFiles.map(pf => 
          uploadFile({ activityId, file: pf.file }).catch(err => {
            logger.error(`Erro ao enviar ${pf.file.name}:`, err);
            return null;
          })
        );
        
        await Promise.all(uploadPromises);
      }

      // Limpa previews
      pendingFiles.forEach(pf => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });


      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['customer-activities', customerId] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });
      
      toast.success(`${config.title.replace('Nova ', '').replace('Novo ', '')} registrada com sucesso`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('Erro ao salvar atividade:', error);
      toast.error('Erro ao salvar atividade');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className={cn(
          "p-0 flex flex-col",
          config.showRichText 
            ? "w-[480px] sm:w-[560px]" 
            : "w-[360px] sm:w-[420px]"
        )}
      >
        {/* Header */}
        <SheetHeader className="p-4 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', config.bgClass)}>
              <Icon className={cn('h-5 w-5', config.color)} />
            </div>
            <div>
              <SheetTitle className="text-base">{config.title}</SheetTitle>
              <SheetDescription className="text-xs">
                Preencha os detalhes da atividade
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Conteúdo com scroll */}
        <ScrollArea className="flex-1">
          <form id="customer-activity-form" onSubmit={form.handleSubmit(onSubmit)} className="p-4 space-y-4">
            {/* Campo Título */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-xs font-medium">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder={config.titlePlaceholder}
                className="h-9 text-sm"
                {...form.register('title')}
              />
              {form.formState.errors.title && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            {/* Campo Descrição - RichTextEditor para notas, Textarea para outros */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                Descrição
              </Label>
              
              {config.showRichText ? (
                <RichTextEditor
                  content={richDescription}
                  onChange={setRichDescription}
                  placeholder="Adicione detalhes sobre a atividade..."
                  minHeight="180px"
                />
              ) : (
                <Textarea
                  id="description"
                  placeholder="Adicione detalhes sobre a atividade..."
                  className="min-h-[100px] text-sm resize-none"
                  {...form.register('description')}
                />
              )}
            </div>

            {/* Campo Data/Hora (apenas para meeting e task) */}
            {config.showSchedule && (
              <div className="space-y-2">
                <Label htmlFor="scheduled_at" className="text-xs font-medium">
                  Data e Hora
                </Label>
                <Input
                  id="scheduled_at"
                  type="datetime-local"
                  className="h-9 text-sm"
                  {...form.register('scheduled_at')}
                />
              </div>
            )}

            {/* Área de Anexos (apenas para notas) */}
            {config.showAttachments && (
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5" />
                  Anexos
                  {pendingFiles.length > 0 && (
                    <span className="text-muted-foreground">
                      ({pendingFiles.length}/10)
                    </span>
                  )}
                </Label>
                
                {/* Zona de upload drag-and-drop */}
                <div 
                  className={cn(
                    "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                    "hover:border-primary/50 hover:bg-muted/30"
                  )}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Arraste arquivos ou clique para adicionar
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                    Máximo 10MB por arquivo
                  </p>
                </div>

                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                
                {/* Lista de arquivos pendentes */}
                {pendingFiles.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {pendingFiles.map(pf => (
                      <div 
                        key={pf.id} 
                        className="relative group aspect-square rounded-md overflow-hidden ring-1 ring-border"
                      >
                        {pf.preview ? (
                          <img 
                            src={pf.preview} 
                            alt={pf.file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex flex-col items-center justify-center p-1">
                            <File className="h-5 w-5 text-muted-foreground mb-0.5" />
                            <span className="text-[8px] text-muted-foreground text-center line-clamp-2">
                              {pf.file.name}
                            </span>
                          </div>
                        )}
                        
                        {/* Botão remover */}
                        <button
                          type="button"
                          onClick={() => removePendingFile(pf.id)}
                          className={cn(
                            "absolute top-0.5 right-0.5 h-4 w-4 rounded-full",
                            "bg-destructive text-destructive-foreground",
                            "opacity-0 group-hover:opacity-100 transition-opacity",
                            "flex items-center justify-center"
                          )}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </ScrollArea>

        {/* Footer com botão */}
        <div className="p-4 pt-3 border-t">
          <Button 
            type="submit"
            form="customer-activity-form"
            className="w-full h-9 text-sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar {config.title.replace('Nova ', '').replace('Novo ', '')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
